import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Avatar,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Fab,
  Tooltip,
  Badge,
  Stack,
  Grid,
  Paper,
  Fade,
  Slide,
  Zoom,
  useTheme,
  useMediaQuery,
  keyframes,
  Switch,
  FormControlLabel,
  SpeedDial,
  SpeedDialIcon,
  SpeedDialAction,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Skeleton,
} from "@mui/material";

import {
  ArrowBack as ArrowBackIcon,
  Share as ShareIcon,
  Key as KeyIcon,
  Delete as DeleteIcon,
  CurrencyExchange as CoinIcon,
  Groups2 as TeamIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  StarBorder as StarIcon,
  EmojiEvents as TrophyIcon,
  Visibility as ViewIcon,
  MoreVert as MoreIcon,
} from "@mui/icons-material";

import {
  sendMessage,
  addListener,
  removeListener,
} from "../services/websocketManager";
import { LottieAnimation } from "../lottie/LottieAnimation.js";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import SendCoins from "./SendCoins";
import Profile from "./Profile.js";
import { Share } from "./Share";
import { GENERAL } from "../general/General";
import { useUser } from "../authMethods/UserContext";
import Swal from "sweetalert2";

// Animations
const slideInLeft = keyframes`
  0% { transform: translateX(-100%); opacity: 0; }
  100% { transform: translateX(0); opacity: 1; }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(79, 70, 229, 0.3); }
  50% { box-shadow: 0 0 20px rgba(79, 70, 229, 0.6); }
`;

const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const pulseOnline = keyframes`
  0%, 100% { 
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }
  50% { 
    transform: scale(1.1);
    box-shadow: 0 0 0 8px rgba(16, 185, 129, 0);
  }
`;

const Favorites = ({ userDataFromApp, updateBackground }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isVerySmall = useMediaQuery(theme.breakpoints.down("sm"));
  
  // State management
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlineOnly, setShowOnlineOnly] = useState(false);
  
  // Dialog states
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sendCoinsOpen, setSendCoinsOpen] = useState(false);
  const [actionDrawerOpen, setActionDrawerOpen] = useState(false);
  
  const [friendUrl, setFriendUrl] = useState(null);
  const [enteredCode, setEnteredCode] = useState("");
  const [recipientUser, setRecipientUser] = useState(null);
  
  const location = useLocation();
  const { userData } = useUser();
  const { joinGame } = location.state || {};

  useEffect(() => {
    updateBackground({ image: GENERAL.BACKGROUND });
    
    const handleWebSocketEvent = (event) => {
      if (event.type === "message" && event.message.messageType === "favorites_data") {
        setFavorites(event.message.favoriteStatistics || []);
        setLoading(false);
      }
    };
    
    addListener(handleWebSocketEvent);
    requestForFavorites();
    
    return () => removeListener(handleWebSocketEvent);
  }, [updateBackground]);

  const requestForFavorites = () => {
    setLoading(true);
    sendMessage({ messageType: "get_favorites" });
  };

  const goBack = () => navigate(-1);

  const handleUserSelect = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.userName === user.userName);
      if (isSelected) {
        return prev.filter(u => u.userName !== user.userName);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredFavorites.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers([...filteredFavorites]);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedUsers.length === 0) {
      Swal.fire({
        title: t("No users selected"),
        text: t("Please select users to delete"),
        icon: "warning",
      });
      return;
    }
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    const favoritesToDelete = selectedUsers.map(user => user.id);
    sendMessage({
      messageType: "remove_from_favorite",
      user: { favorites: favoritesToDelete },
    });
    setSelectedUsers([]);
    setDeleteDialogOpen(false);
    Swal.fire({
      title: t("Success"),
      text: t("Selected friends have been removed"),
      icon: "success",
      timer: 2000,
      showConfirmButton: false,
    });
  };

  const handleSendCoins = () => {
    if (selectedUsers.length !== 1) {
      Swal.fire({
        title: t("Select one user"),
        text: t("Please select exactly one user to send coins to"),
        icon: "warning",
      });
      return;
    }
    setRecipientUser(selectedUsers[0]);
    setSendCoinsOpen(true);
  };

  const handleInviteCode = () => {
    if (!enteredCode || enteredCode.length !== 6) {
      Swal.fire({
        title: t("Invalid Code"),
        text: t("Please enter a valid 6-digit invite code"),
        icon: "error",
      });
      return;
    }
    sendMessage({
      messageType: "add_to_favorite_by_key",
      code: enteredCode,
    });
    setCodeDialogOpen(false);
    setEnteredCode("");
  };

  const inviteToJoin = () => {
    const id = userData?.id || userDataFromApp?.id;
    const gameURL = GENERAL.HOST + "welcome?friend=" + id;
    setFriendUrl(gameURL);
    setShareDialogOpen(true);
  };

  const handleAvatarClick = (user) => {
    setSelectedAvatar(user);
    setProfileOpen(true);
  };

  // Filter favorites based on search and online status
  const filteredFavorites = favorites.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.userName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesOnline = !showOnlineOnly || user.online;
    return matchesSearch && matchesOnline;
  });

  const UserCard = ({ user, index }) => {
    const isSelected = selectedUsers.some(u => u.userName === user.userName);
    const totalGames = user.userStatistics?.totalGames || 0;
    const totalWins = user.userStatistics?.totalWins || 0;
    const totalLosses = user.userStatistics?.totalLose || 0;
    const totalDraws = user.userStatistics?.totalDraw || 0;
    
    // Calculate win rate as percentage (like in profile)
    const winRate = totalGames > 0 ? ((totalWins / totalGames) * 100).toFixed(1) : "0.0";
    
    const isPerfect = winRate === "100.0" && totalGames > 0;

    return (
      <Fade in timeout={400 + index * 100}>
        <Card
          sx={{
            minHeight: "300px",
            position: "relative",
            cursor: "pointer",
            transition: "all 0.3s ease",
            transform: isSelected ? "scale(1.02)" : "scale(1)",
            background: isSelected 
              ? "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)"
              : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            border: isSelected 
              ? "2px solid #ffd700" 
              : "2px solid transparent",
            borderRadius: "20px",
            overflow: "hidden",
            height: { xs: 180, md: 200 }, // Fixed height for all cards
            width: "100%",
            "&:hover": {
              transform: "translateY(-4px) scale(1.02)",
              boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
            },
            "&::before": isSelected ? {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "linear-gradient(45deg, transparent 30%, rgba(255,215,0,0.1) 50%, transparent 70%)",
              animation: `${pulseGlow} 2s infinite`,
            } : {},
          }}
          onClick={() => handleUserSelect(user)}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 }, height: "100%", display: "flex", flexDirection: "column" }}>
            {/* Selection Indicator - Top Left */}
            {isSelected && (
              <Zoom in>
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    left: 12,
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: "#ffd700",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                    zIndex: 2,
                  }}
                >
                  <CheckIcon sx={{ fontSize: 18, color: "#1a1a1a" }} />
                </Box>
              </Zoom>
            )}

            {/* Top Section - Avatar and Name */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, pt: isSelected ? 2 : 0 }}>
              <Box sx={{ position: "relative", flexShrink: 0 }}>
                <Avatar
                  src={user.url}
                  alt={user.name}
                  sx={{
                    width: { xs: 50, md: 60 },
                    height: { xs: 50, md: 60 },
                    border: `3px solid ${isSelected ? "#ffd700" : "#e2e8f0"}`,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.1)",
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAvatarClick(user);
                  }}
                />
                
                {/* Online Status with Pulsing Animation */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -2,
                    right: -2,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: user.online ? "#10b981" : "#ef4444",
                    border: "2px solid white",
                    animation: user.online 
                      ? `${pulseOnline} 2s infinite` 
                      : "none",
                    zIndex: 1,
                  }}
                />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "0.7rem", md: "0.7rem" },
                    color: isSelected ? "white" : "text.primary",
                    mb: 0.5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: isSelected ? "normal" : "nowrap", // Show full name when selected
                    lineHeight: 1.2,
                  }}
                >
                  {user.name}
                </Typography>
                
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    icon={user.online ? <CheckIcon sx={{ fontSize: 14 }} /> : undefined}
                    label={user.online ? t("Online") : t("Offline")}
                    size="small"
                    sx={{
                      backgroundColor: user.online ? "#dcfce7" : "#fee2e2",
                      color: user.online ? "#166534" : "#991b1b",
                      fontSize: "0.7rem",
                      height: 20,
                    }}
                  />
                  
                  {isPerfect && (
                    <Chip
                      icon={<TrophyIcon sx={{ fontSize: 12 }} />}
                      label={t("Perfect")}
                      size="small"
                      sx={{
                        backgroundColor: "#fef3c7",
                        color: "#92400e",
                        fontSize: "0.7rem",
                        height: 20,
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            {/* Win Rate Display - Centered */}
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.2rem", md: "1.2rem" },
                  color: isSelected ? "#ffd700" : "#4f46e5",
                  lineHeight: 1,
                  mb: 0.5,
                }}
              >
                {winRate}%
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: isSelected ? "rgba(255,255,255,0.8)" : "text.secondary",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                }}
              >
                {t("Win Rate")}
              </Typography>
            </Box>

            {/* Stats Grid - Bottom Section */}
            <Box sx={{ mt: "auto" }}>
              <Box 
                sx={{ 
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 1,
                  pt: 1.5,
                  borderTop: `1px solid ${isSelected ? "rgba(255,255,255,0.2)" : "#e2e8f0"}`,
                }}
              >
                {/* Wins */}
                <Box sx={{ textAlign: "center" }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 700,
                      color: isSelected ? "white" : "#10b981",
                      fontSize: { xs: "1rem", md: "1.1rem" },
                      lineHeight: 1,
                    }}
                  >
                    {totalWins}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: isSelected ? "rgba(255,255,255,0.7)" : "text.secondary",
                      fontSize: "0.7rem",
                      display: "block",
                      lineHeight: 1,
                    }}
                  >
                    {t("Wins")}
                  </Typography>
                </Box>
                
                {/* Losses */}
                <Box sx={{ textAlign: "center" }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 700,
                      color: isSelected ? "white" : "#ef4444",
                      fontSize: { xs: "1rem", md: "1.1rem" },
                      lineHeight: 1,
                    }}
                  >
                    {totalLosses}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: isSelected ? "rgba(255,255,255,0.7)" : "text.secondary",
                      fontSize: "0.7rem",
                      display: "block",
                      lineHeight: 1,
                    }}
                  >
                    {t("Losses")}
                  </Typography>
                </Box>
                
                {/* Draws */}
                <Box sx={{ textAlign: "center" }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 700,
                      color: isSelected ? "white" : "#f59e0b",
                      fontSize: { xs: "1rem", md: "1.1rem" },
                      lineHeight: 1,
                    }}
                  >
                    {totalDraws}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: isSelected ? "rgba(255,255,255,0.7)" : "text.secondary",
                      fontSize: "0.7rem",
                      display: "block",
                      lineHeight: 1,
                    }}
                  >
                    {t("Draws")}
                  </Typography>
                </Box>

                {/* Total Games */}
                <Box sx={{ textAlign: "center" }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 700,
                      color: isSelected ? "white" : "#6366f1",
                      fontSize: { xs: "1rem", md: "1.1rem" },
                      lineHeight: 1,
                    }}
                  >
                    {totalGames}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: isSelected ? "rgba(255,255,255,0.7)" : "text.secondary",
                      fontSize: "0.7rem",
                      display: "block",
                      lineHeight: 1,
                    }}
                  >
                    {t("Total Games")}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Fade>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ borderRadius: "16px", overflow: "hidden" }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Skeleton variant="circular" width={60} height={60} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="80%" height={24} />
                      <Skeleton variant="text" width="60%" height={20} />
                    </Box>
                    <Skeleton variant="text" width={40} height={24} />
                  </Box>
                  <Skeleton variant="rectangular" width="100%" height={20} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        p: { xs: 1, md: 2 },
      }}
    >
      {/* Header */}
      <Paper
        elevation={8}
        sx={{
          borderRadius: "20px",
          mb: 3,
          overflow: "hidden",
          background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
          color: "white",
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton
                onClick={goBack}
                sx={{
                  background: "rgba(255,255,255,0.1)",
                  color: "white",
                  "&:hover": { background: "rgba(255,255,255,0.2)" },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {t("Friends")}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {filteredFavorites.length} {t("friends found")}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip title={t("Refresh")}>
                <IconButton
                  onClick={requestForFavorites}
                  sx={{
                    background: "rgba(255,255,255,0.1)",
                    color: "white",
                    "&:hover": { background: "rgba(255,255,255,0.2)" },
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              
              {isMobile && selectedUsers.length > 0 && (
                <Tooltip title={t("Actions")}>
                  <IconButton
                    onClick={() => setActionDrawerOpen(true)}
                    sx={{
                      background: "rgba(255,215,0,0.2)",
                      color: "#ffd700",
                      "&:hover": { background: "rgba(255,215,0,0.3)" },
                    }}
                  >
                    <Badge badgeContent={selectedUsers.length} color="error">
                      <MoreIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Search and Filters */}
          <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", md: "row" } }}>
            <TextField
              placeholder={t("Search friends...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  background: "rgba(255,255,255,0.1)",
                  color: "white",
                  borderRadius: "12px",
                  "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                  "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" },
                  "&.Mui-focused fieldset": { borderColor: "#ffd700" },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "rgba(255,255,255,0.7)",
                },
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: "rgba(255,255,255,0.7)", mr: 1 }} />,
              }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={showOnlineOnly}
                  onChange={(e) => setShowOnlineOnly(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-thumb": {
                      backgroundColor: showOnlineOnly ? "#ffd700" : "white",
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor: showOnlineOnly ? "rgba(255,215,0,0.5)" : "rgba(255,255,255,0.3)",
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ color: "white", fontSize: "0.9rem" }}>
                  {t("Online only")}
                </Typography>
              }
              sx={{ color: "white", mr: 0 }}
            />
          </Box>

          {/* Action Buttons - Desktop */}
          {!isMobile && (
            <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                startIcon={<ShareIcon />}
                onClick={inviteToJoin}
                sx={{
                  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  borderRadius: "12px",
                }}
              >
                {t("Invite Friends")}
              </Button>
              
              <Button
                variant="contained"
                startIcon={<KeyIcon />}
                onClick={() => setCodeDialogOpen(true)}
                sx={{
                  background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  borderRadius: "12px",
                }}
              >
                {t("Enter Code")}
              </Button>
              
              {selectedUsers.length > 0 && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteSelected}
                    sx={{
                      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      borderRadius: "12px",
                    }}
                  >
                    {t("Delete")} ({selectedUsers.length})
                  </Button>
                  
                  <Button
                    variant="contained"
                    startIcon={<CoinIcon />}
                    onClick={handleSendCoins}
                    disabled={selectedUsers.length !== 1}
                    sx={{
                      background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                      borderRadius: "12px",
                    }}
                  >
                    {t("Send Coins")}
                  </Button>
                  
                  <Button
                    variant="contained"
                    startIcon={<TeamIcon />}
                    onClick={() => navigate("/team-builder")}
                    sx={{
                      background: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
                      borderRadius: "12px",
                    }}
                  >
                    {t("Team Play")}
                  </Button>
                </>
              )}
            </Box>
          )}

          {/* Select All */}
          {filteredFavorites.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Button
                size="small"
                onClick={handleSelectAll}
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  textTransform: "none",
                  "&:hover": { background: "rgba(255,255,255,0.1)" },
                }}
              >
                {selectedUsers.length === filteredFavorites.length 
                  ? t("Deselect All") 
                  : t("Select All")
                } ({filteredFavorites.length})
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Content */}
      {filteredFavorites.length === 0 ? (
        <Paper
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: "20px",
            background: "rgba(255,255,255,0.95)",
          }}
        >
          <Box sx={{ mb: 3 }}>
            <PersonIcon sx={{ fontSize: 80, color: "#e2e8f0" }} />
          </Box>
          <Typography variant="h5" fontWeight="bold" mb={2}>
            {favorites.length === 0 
              ? t("No friends yet!")
              : t("No friends match your search")
            }
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            {favorites.length === 0 
              ? t("Start by inviting friends or entering an invite code")
              : t("Try adjusting your search terms or filters")
            }
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              startIcon={<ShareIcon />}
              onClick={inviteToJoin}
              sx={{ borderRadius: "12px" }}
            >
              {t("Invite Friends")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<KeyIcon />}
              onClick={() => setCodeDialogOpen(true)}
              sx={{ borderRadius: "12px" }}
            >
              {t("Enter Code")}
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredFavorites.map((user, index) => (
            <Grid item xs={12} sm={6} lg={4} xl={3} key={user.userName}>
              <UserCard user={user} index={index} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Mobile Action Drawer */}
      <Drawer
        anchor="bottom"
        open={actionDrawerOpen}
        onClose={() => setActionDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "20px 20px 0 0",
            background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
            color: "white",
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" mb={2}>
            {t("Actions")} ({selectedUsers.length} {t("selected")})
          </Typography>
          
          <List>
            <ListItemButton onClick={handleDeleteSelected}>
              <ListItemIcon sx={{ color: "#ef4444" }}>
                <DeleteIcon />
              </ListItemIcon>
              <ListItemText primary={t("Delete Selected")} />
            </ListItemButton>
            
            <ListItemButton 
              onClick={handleSendCoins}
              disabled={selectedUsers.length !== 1}
            >
              <ListItemIcon sx={{ color: selectedUsers.length === 1 ? "#8b5cf6" : "#6b7280" }}>
                <CoinIcon />
              </ListItemIcon>
              <ListItemText 
                primary={t("Send Coins")}
                secondary={selectedUsers.length !== 1 ? t("Select exactly one user") : ""}
              />
            </ListItemButton>
            
            <ListItemButton onClick={() => navigate("/team-builder")}>
              <ListItemIcon sx={{ color: "#06b6d4" }}>
                <TeamIcon />
              </ListItemIcon>
              <ListItemText primary={t("Build Team")} />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>

      {/* Floating Action Buttons - Mobile */}
      {isMobile && (
        <Box sx={{ position: "fixed", bottom: 20, right: 20, zIndex: 1000 }}>
          <Stack spacing={2}>
            <Fab
              color="primary"
              onClick={inviteToJoin}
              sx={{
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                },
              }}
            >
              <ShareIcon />
            </Fab>
            
            <Fab
              color="secondary"
              onClick={() => setCodeDialogOpen(true)}
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                },
              }}
            >
              <KeyIcon />
            </Fab>
          </Stack>
        </Box>
      )}

      {/* Dialogs */}
      <Dialog 
        open={shareDialogOpen} 
        onClose={() => setShareDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)",
            backdropFilter: "blur(20px)",
          }
        }}
      >
        <DialogTitle sx={{ 
          background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
          color: "white",
          borderRadius: "16px 16px 0 0"
        }}>
          {t("Invite Your Friends")}
        </DialogTitle>
        <DialogContent>
          <Share currentUrl={friendUrl} />
        </DialogContent>
      </Dialog>

      <Dialog 
        open={codeDialogOpen} 
        onClose={() => setCodeDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)",
            backdropFilter: "blur(20px)",
          }
        }}
      >
        <DialogTitle sx={{ 
          background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
          color: "white",
          borderRadius: "16px 16px 0 0"
        }}>
          {t("Enter Invite Code")}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t("Enter the 6-digit code sent by your friend")}
          </Typography>
          <TextField
            fullWidth
            type="text"
            inputProps={{ maxLength: 6 }}
            value={enteredCode}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d{0,6}$/.test(value)) {
                setEnteredCode(value);
              }
            }}
            placeholder={t("Enter 6-digit code")}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                fontSize: "1.2rem",
                textAlign: "center",
                letterSpacing: "0.2em",
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setCodeDialogOpen(false)}
            sx={{ borderRadius: "12px" }}
          >
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleInviteCode}
            variant="contained"
            disabled={enteredCode.length !== 6}
            sx={{
              borderRadius: "12px",
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            }}
          >
            {t("Add Friend")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)",
            backdropFilter: "blur(20px)",
          }
        }}
      >
        <DialogTitle sx={{ 
          background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
          color: "white",
          borderRadius: "16px 16px 0 0"
        }}>
          {t("Confirm Delete")}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" mb={2}>
            {t("Are you sure you want to remove")} 
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("This action cannot be undone")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ borderRadius: "12px" }}
          >
            {t("Cancel")}
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            sx={{
              borderRadius: "12px",
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
            }}
          >
            {t("Delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={sendCoinsOpen} 
        onClose={() => setSendCoinsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)",
            backdropFilter: "blur(20px)",
          }
        }}
      >
        <DialogTitle sx={{
          background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
          color: "white",
          borderRadius: "16px 16px 0 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          {t("Send Coins")}
          <IconButton
            onClick={() => setSendCoinsOpen(false)}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {recipientUser && (
            <SendCoins
              preselectedUser={recipientUser}
              onSuccess={() => setSendCoinsOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Profile
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        avatar={selectedAvatar}
        userData={userDataFromApp}
      />
    </Box>
  );
};

export default Favorites;